/**
 * Turns whatever the registration functions return or throw into a message the user can act on, plus a short code
 * that is sent to Raygun so failures can be counted.
 */
export interface RegistrationError {
  code: string;
  message: string;
}

const CONTACT = 'Please contact support.';

/** `error` is the `error` field of a function result (string or Recurly error object), or a thrown error. */
export function describeRegistrationError(error: any): RegistrationError {
  if (error === null || error === undefined || error === '') {
    return { code: 'UNKNOWN', message: `We could not finish your registration. ${CONTACT}` };
  }

  // Thrown by the callable itself (network, internal error, unauthenticated).
  if (typeof error === 'object' && typeof error.code === 'string' && error.code.startsWith('functions/')) {
    return {
      code: 'REQUEST_FAILED',
      message: `We could not reach the registration service. Please try again in a moment. If it keeps happening, ${CONTACT.toLowerCase()}`,
    };
  }

  const raw: string =
    typeof error === 'string' ? error : (error.params && error.params[0] && error.params[0].message) || error.message || '';

  if (/cannot purchase on canceled account|account is closed|closed account/i.test(raw)) {
    return {
      code: 'ACCOUNT_CLOSED',
      message:
        'This email already had a billing account that was closed. Contact support to reopen it, or register with a different email.',
    };
  }
  if (/already has an active subscription/i.test(raw)) {
    return { code: 'ACTIVE_SUBSCRIPTION', message: raw };
  }
  if (/already (exists|been taken|in use)|code.*(taken|exists)/i.test(raw)) {
    return {
      code: 'ACCOUNT_EXISTS',
      message: `A billing account already exists for this email. ${CONTACT}`,
    };
  }
  if (raw) {
    return { code: 'RECURLY_ERROR', message: `We could not finish your registration: ${raw} ${CONTACT}` };
  }
  return { code: 'UNKNOWN', message: `We could not finish your registration. ${CONTACT}` };
}

/** Sends the failure to Raygun when it is loaded. Never throws. */
export function reportRegistrationFailure(step: string, error: RegistrationError, detail?: any): void {
  try {
    console.error(`[registration] ${step} failed (${error.code})`, detail);
    const rg4js = typeof window !== 'undefined' ? (window as any).rg4js : undefined;
    if (rg4js) {
      rg4js('send', {
        error: new Error(`Registration failed: ${step} (${error.code})`),
        customData: { step, code: error.code, message: error.message },
        tags: ['registration', error.code],
      });
    }
  } catch (e) {
    // Reporting must never break the registration screen.
  }
}
